import { useState, type FormEvent } from 'react';
import { parametrosFiscais, type ParametrosFiscais } from '@raiox/domain';
import { trpc } from '../lib/trpc.js';
import { Carregando, Erro } from '../componentes/Estados.js';
import { data, dataHora, numero } from '../lib/formato.js';
import type { Papel } from '../lib/tipos.js';

type Chave = keyof ParametrosFiscais;

const CHAVES = Object.keys(parametrosFiscais.shape) as Chave[];

const ROTULOS: Record<Chave, string> = {
  aliquotaCbs: 'Alíquota de teste da CBS (%)',
  aliquotaIbs: 'Alíquota de teste do IBS (%)',
  aliquotaInss: 'Retenção previdenciária (%)',
  aliquotaInssDesonerada: 'Retenção previdenciária desonerada (%)',
  pisoPisCofinsCsll: 'Piso para PIS/COFINS/CSLL (R$)',
  toleranciaAliquota: 'Tolerância nas conferências (p.p.)',
  anoInicioRtc: 'Ano de início do grupo IBS/CBS',
};

function hoje(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Ano é ordinal, não quantidade: 2026 não pode virar "2.026". */
function valorParametro(chave: string, valor: number): string {
  return chave === 'anoInicioRtc' ? String(Math.trunc(valor)) : numero(valor, 4);
}

function FormularioDefinir() {
  const utils = trpc.useUtils();
  const [chave, setChave] = useState<Chave>('aliquotaCbs');
  const [valor, setValor] = useState('');
  const [vigenciaInicio, setVigenciaInicio] = useState(hoje());
  const [observacao, setObservacao] = useState('');

  const definir = trpc.parametros.definir.useMutation({
    onSuccess: () => {
      setValor('');
      setObservacao('');
      void utils.parametros.invalidate();
    },
  });

  function enviar(e: FormEvent) {
    e.preventDefault();
    const numerico = Number.parseFloat(valor.replace(',', '.'));
    if (!Number.isFinite(numerico)) return;
    definir.mutate({
      chave,
      valor: numerico,
      vigenciaInicio,
      observacao: observacao.trim() === '' ? undefined : observacao.trim(),
    });
  }

  return (
    <form className="params" style={{ marginTop: 14 }} onSubmit={enviar}>
      <h2 style={{ fontSize: 15 }}>Nova vigência</h2>
      <p style={{ margin: '6px 0 0', color: 'var(--ink-2)', fontSize: 12.5, maxWidth: '80ch' }}>
        Gravar um parâmetro não altera o anterior: a vigência aberta é fechada na véspera e uma linha nova
        passa a valer. O histórico continua legível para reconstituir o que valia numa data.
      </p>
      <div className="grid">
        <div>
          <label htmlFor="p-chave">Parâmetro</label>
          <select id="p-chave" value={chave} onChange={(e) => setChave(e.target.value as Chave)}>
            {CHAVES.map((c) => (
              <option key={c} value={c}>
                {ROTULOS[c]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="p-valor">Valor</label>
          <input
            id="p-valor"
            inputMode="decimal"
            required
            value={valor}
            onChange={(e) => setValor(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="p-inicio">Início da vigência</label>
          <input
            id="p-inicio"
            type="date"
            required
            value={vigenciaInicio}
            onChange={(e) => setVigenciaInicio(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="p-obs">Observação</label>
          <input
            id="p-obs"
            maxLength={255}
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
          />
        </div>
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button type="submit" className="btn primary" disabled={definir.isPending}>
          {definir.isPending ? 'Gravando…' : 'Gravar vigência'}
        </button>
        {definir.error ? (
          <span style={{ color: 'var(--crit)', fontSize: 12.5 }} role="alert">
            {definir.error.message}
          </span>
        ) : null}
        {definir.isSuccess ? (
          <span style={{ color: 'var(--ok)', fontSize: 12.5 }}>Vigência gravada.</span>
        ) : null}
      </div>
    </form>
  );
}

export function Parametros({ papel }: { papel: Papel }) {
  const vigentes = trpc.parametros.vigentes.useQuery({});
  const historico = trpc.parametros.historico.useQuery();

  return (
    <section className="panel">
      <div className="params">
        <h2 style={{ fontSize: 15 }}>Parâmetros das verificações</h2>
        <p style={{ margin: '6px 0 0', color: 'var(--ink-2)', fontSize: 12.5, maxWidth: '80ch' }}>
          Estes valores alimentam a aba de divergências. Ajuste conforme o período e o enquadramento — nada
          aqui altera os valores lidos do XML.
        </p>

        {vigentes.isPending ? <Carregando linhas={3} rotulo="Carregando os parâmetros" /> : null}
        {vigentes.error ? (
          <Erro erro={vigentes.error} aoTentarDeNovo={() => void vigentes.refetch()} />
        ) : null}

        {vigentes.data ? (
          <div className="grid">
            {CHAVES.map((c) => (
              <div key={c}>
                <label htmlFor={`v-${c}`}>{ROTULOS[c]}</label>
                <input id={`v-${c}`} readOnly value={valorParametro(c, vigentes.data[c])} />
              </div>
            ))}
          </div>
        ) : null}

        <div className="eyebrow" style={{ marginTop: 28 }}>
          O que cada verificação faz
        </div>
        <ul>
          <li>
            <strong>CNO ausente</strong> — nota de item 7.02, 7.04, 7.05, 7.15, 7.16, 7.17 ou 7.19 sem
            código de obra no XML.
          </li>
          <li>
            <strong>INSS não retido / fora do padrão</strong> — construção civil sem retenção, ou retenção
            que não fecha em 11% nem em 3,5% sobre nenhuma das bases lidas.
          </li>
          <li>
            <strong>ISS</strong> — retenção indicada sem valor, serviço sem ISS destacado, e conferência de
            base × alíquota.
          </li>
          <li>
            <strong>PIS/COFINS/CSLL</strong> — serviço acima do piso sem retenção de 4,65% destacada.
          </li>
          <li>
            <strong>IBS/CBS</strong> — ausência do grupo em documentos de 2026 em diante, e destaque fora da
            alíquota de transição parametrizada.
          </li>
          <li>
            <strong>ICMS</strong> — CST/CSOSN de operação tributada com valor zerado, e soma dos itens
            diferente do total da nota.
          </li>
        </ul>
      </div>

      {papel === 'admin' ? <FormularioDefinir /> : null}

      <div className="eyebrow">Histórico de vigências</div>
      {historico.isPending ? <Carregando linhas={4} rotulo="Carregando o histórico" /> : null}
      {historico.error ? (
        <Erro erro={historico.error} aoTentarDeNovo={() => void historico.refetch()} />
      ) : null}
      {historico.data ? (
        <>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Parâmetro</th>
                  <th>Escopo</th>
                  <th className="r">Valor</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Observação</th>
                  <th>Gravado em</th>
                </tr>
              </thead>
              <tbody>
                {historico.data.map((l) => (
                  <tr key={l.id}>
                    <td className="strong">{ROTULOS[l.chave as Chave] ?? l.chave}</td>
                    <td>
                      <span className="badge tipo">{l.escopo}</span>
                    </td>
                    <td className="num r">{valorParametro(l.chave, l.valor)}</td>
                    <td className="num nowrap">{data(l.vigenciaInicio)}</td>
                    <td className="num nowrap">
                      {l.vigenciaFim === null ? <span className="badge ok">em vigor</span> : data(l.vigenciaFim)}
                    </td>
                    <td>{l.observacao ?? <span className="muted">—</span>}</td>
                    <td className="num nowrap">{dataHora(l.criadoEm)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ margin: '10px 0 0', fontSize: 11.5, color: 'var(--ink-3)', maxWidth: '92ch' }}>
            Nenhuma linha desta tabela é editada no lugar. Trocar uma alíquota cria um registro novo e fecha
            a vigência anterior, para que um documento antigo possa ser reavaliado com a regra da época.
          </p>
        </>
      ) : null}
    </section>
  );
}
